import React, { useState, useEffect } from "react";
import axios from "axios";
import { BASE_URL, colors } from "../shared/constants";
import { inputStyle, labelStyle, fieldStyle } from "../shared/styles";
import { formatDayLabel } from "../shared/dateUtils";
import { WizardModal } from "./WizardModal";
import { DayPicker, TimesStep, useSlotSetup, startingCursor } from "./SlotSetup";

const PORTAL = `${BASE_URL}/portal/api`;

const STEP_TITLES = {
  1: "New booking campaign",
  2: "Choose inspection days",
  3: "Set times and interval",
};

// A campaign runs against one tenant asset register — a site can hold several
// (Work Pods, Factory Units) and each has its own tenancies. Site, then the
// register, then the days it will actually be worked.
export function CampaignModal({ onClose, onSaved }) {
  const [step, setStep] = useState(1);
  const [sites, setSites] = useState([]);
  const [registers, setRegisters] = useState([]);
  const [unfiled, setUnfiled] = useState(0);
  const [form, setForm] = useState({ site: "", area: "", name: "" });
  const [siteSearch, setSiteSearch] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const setup = useSlotSetup();
  const [cursor, setCursor] = useState(() => startingCursor(null));

  useEffect(() => {
    axios
      .get(`${PORTAL}/campaigns/sites/`)
      .then((r) => setSites(r.data || []))
      .catch(() => setError("Could not load sites."));
  }, []);

  // Registers belong to a site, so they reload whenever the site changes.
  useEffect(() => {
    if (!form.site) {
      setRegisters([]);
      setUnfiled(0);
      return;
    }
    axios
      .get(`${PORTAL}/campaigns/registers/?site=${form.site}`)
      .then((r) => {
        setRegisters(r.data?.registers || []);
        setUnfiled(r.data?.unfiled_count || 0);
      })
      .catch(() => setError("Could not load this site's registers."));
  }, [form.site]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const pickSite = (value) =>
    setForm((f) => ({ ...f, site: value, area: "", name: "" }));

  const chosenSite = sites.find((s) => String(s.id) === String(form.site));

  // Name and address both, the same as the inspection site search — people
  // look a building up by whichever they have to hand.
  const siteMatches = (() => {
    const q = siteSearch.trim().toLowerCase();
    if (!q) return sites;
    return sites.filter((s) =>
      `${s.site_name} ${s.site_address}`.toLowerCase().includes(q)
    );
  })();

  // The campaign is the register, so name it after it — still editable.
  const pickRegister = (value) => {
    const reg = registers.find((x) => String(x.id) === String(value));
    setForm((f) => ({
      ...f,
      area: value,
      name: f.name.trim() ? f.name : reg ? reg.name : "",
    }));
  };

  const next = () => {
    setError("");
    if (step === 1) {
      if (!form.site) return setError("Choose a site.");
      if (!form.area) return setError("Choose an asset register.");
      if (!form.name.trim()) return setError("Give the campaign a name.");
      return setStep(2);
    }
    if (step === 2) {
      if (!setup.days.length)
        return setError("Pick at least one day the inspection will run.");
      return setStep(3);
    }
    return create();
  };

  const create = async () => {
    setError("");
    if (setup.badDay)
      return setError(
        `${formatDayLabel(setup.badDay)}: the finish time must be after the start.`
      );
    if (!setup.total) return setError("That gives no whole slots. Try a shorter interval.");

    setSaving(true);
    // The window is whatever the chosen days span — the days between may well
    // not be worked, which is the point of picking them individually.
    const days = setup.days;
    try {
      const created = await axios.post(`${PORTAL}/campaigns/`, {
        site: Number(form.site),
        area: Number(form.area),
        name: form.name,
        window_start: days[0],
        window_end: days[days.length - 1],
        status: "open",
      });
      try {
        await axios.post(`${PORTAL}/campaigns/${created.data.id}/generate-slots/`, {
          days: setup.payloadDays(),
          interval_minutes: setup.slotMinutes,
          capacity: Number(setup.capacity) || 1,
        });
      } catch (slotError) {
        // Times are only ever set here, so a campaign without them could never
        // be finished. Undo the whole thing rather than leave one half made.
        await axios.delete(`${PORTAL}/campaigns/${created.data.id}/`).catch(() => {});
        setError(
          slotError.response?.data?.detail ||
            "Could not create the inspection times, so nothing was saved. Try again."
        );
        setSaving(false);
        return;
      }
      onSaved(created.data);
    } catch (e) {
      setError(e.response?.data?.detail || "Could not create the campaign.");
      setSaving(false);
    }
  };

  return (
    <WizardModal
      title={STEP_TITLES[step]}
      subtitle={step > 1 ? form.name : null}
      onClose={onClose}
      backLabel={step === 1 ? "Cancel" : "Back"}
      onBack={step === 1 ? onClose : () => setStep(step - 1)}
      nextLabel={step < 3 ? "Next" : saving ? "Creating..." : "Create campaign"}
      nextDisabled={saving}
      onNext={next}
    >
      {step === 1 && (
        <>
          <div style={fieldStyle}>
            <label style={labelStyle}>Site *</label>

            {chosenSite ? (
              // Collapsed once answered, so the step reads as done and the
              // modal does not stay tall while you fill in the rest.
              <div
                style={{
                  border: `1px solid ${colors.border}`,
                  borderRadius: "8px",
                  padding: "10px 12px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: "12px",
                }}
              >
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontWeight: "500", color: colors.text }}>
                    {chosenSite.site_name}
                  </div>
                  <div style={{ fontSize: "12px", color: colors.slate }}>
                    {chosenSite.site_address}
                  </div>
                </div>
                <button
                  onClick={() => {
                    pickSite("");
                    setSiteSearch("");
                  }}
                  style={{
                    background: "none",
                    border: "none",
                    padding: 0,
                    cursor: "pointer",
                    color: colors.activeBlue,
                    fontSize: "13px",
                    whiteSpace: "nowrap",
                  }}
                >
                  Change
                </button>
              </div>
            ) : (
              <>
                <input
                  style={inputStyle}
                  placeholder="Search by site name or address..."
                  value={siteSearch}
                  onChange={(e) => setSiteSearch(e.target.value)}
                  autoFocus
                />
                <div
                  style={{
                    marginTop: "8px",
                    maxHeight: "220px",
                    overflowY: "auto",
                    border: `1px solid ${colors.border}`,
                    borderRadius: "8px",
                  }}
                >
                  {sites.length === 0 ? (
                    <div style={hint}>No sites yet.</div>
                  ) : siteMatches.length === 0 ? (
                    <div style={hint}>Nothing matches that search.</div>
                  ) : (
                    siteMatches.map((s) => (
                      <SiteOption
                        key={s.id}
                        site={s}
                        onPick={() => pickSite(String(s.id))}
                      />
                    ))
                  )}
                </div>
              </>
            )}
          </div>

          <div style={fieldStyle}>
            <label style={labelStyle}>Asset register *</label>
            <select
              style={inputStyle}
              value={form.area}
              onChange={(e) => pickRegister(e.target.value)}
              disabled={!form.site}
            >
              <option value="">
                {form.site ? "Choose a register..." : "Choose a site first"}
              </option>
              {registers.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.name} ({r.unit_count} tenant{r.unit_count !== 1 ? "s" : ""})
                </option>
              ))}
            </select>
            {form.site && registers.length === 0 && (
              <div style={{ fontSize: "12px", color: colors.slate, marginTop: "6px" }}>
                This site has no tenant registers yet. Add one under Site → Asset
                Register first.
              </div>
            )}
            {unfiled > 0 && (
              // These predate registers and sit in no tab, so they would silently
              // be left out. Say so rather than let it be a surprise.
              <div style={{ fontSize: "12px", color: colors.slate, marginTop: "6px" }}>
                {unfiled} tenant{unfiled !== 1 ? "s" : ""} at this site belong to no
                register and won't be included.
              </div>
            )}
          </div>

          <div style={fieldStyle}>
            <label style={labelStyle}>Name *</label>
            <input
              style={inputStyle}
              value={form.name}
              onChange={(e) => set("name", e.target.value)}
              placeholder="Named after the register by default"
            />
          </div>
        </>
      )}

      {step === 2 && (
        <DayPicker
          setup={setup}
          cursor={cursor}
          onCursorChange={setCursor}
          hint="Click each day the inspection will run. Days you skip are simply not worked."
        />
      )}

      {step === 3 && <TimesStep setup={setup} />}

      {error && (
        <div style={{ color: colors.red, fontSize: "13px", marginTop: "12px" }}>
          {error}
        </div>
      )}
    </WizardModal>
  );
}

const hint = {
  padding: "14px 12px",
  fontSize: "13px",
  color: colors.slate,
};

// One site in the search results. Address underneath, and a quiet note when
// the site has no tenancies — that used to be the reason such sites were
// hidden from this picker entirely, which is what made empty buildings
// impossible to get a campaign for.
function SiteOption({ site, onPick }) {
  const [hover, setHover] = useState(false);
  return (
    <div
      onClick={onPick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        padding: "10px 12px",
        borderBottom: `1px solid ${colors.border}`,
        backgroundColor: hover ? "#f8fafc" : colors.white,
        cursor: "pointer",
      }}
    >
      <div style={{ fontWeight: "500", color: colors.text, fontSize: "14px" }}>
        {site.site_name}
      </div>
      <div style={{ fontSize: "12px", color: colors.slate }}>
        {site.site_address}
        {site.unit_count === 0 ? " · No tenancies yet" : ""}
      </div>
    </div>
  );
}
